'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type { JSX } from 'react';
import { ResolvedEventAssociation } from '@/components/live/resolved-event-association';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Panel } from '@/components/ui/panel';
import { Link } from '@/i18n/navigation';
import { buildResolvedPanelViewModel, type ResolvedPanelProps } from './derive';
import { OtherOutcomesTable } from './other-outcomes-table';
import { StatStrip } from './stat-strip';
import { WinnerHeader } from './winner-header';
import { WinnerPageHeader } from './winner-page-header';

/**
 * Resolved market panel (Figma 3928-816): a winner header, a 4-tile stat strip
 * snapshotted at resolution, and the outcomes table. Pure render of
 * `buildResolvedPanelViewModel`.
 *
 * `'market'` is the standalone resolved surface, `'market-tab'` places the
 * same winner content in the market detail tabs, and `'winner-page'` recaps
 * the parent market on `/winners/{mint}`.
 */
export function ResolvedPanel(props) {
  const $ = _c(56);
  const t = useTranslations("market.resolvedPanel");
  let t0;
  if ($[0] !== props) {
    t0 = buildResolvedPanelViewModel(props);
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const vm = t0;
  if (vm === null) {
    return null;
  }
  const isWinnerPage = props.variant === "winner-page";
  const isBare = isWinnerPage || props.variant === "market-tab";
  let t1;
  if ($[2] !== isWinnerPage || $[3] !== props.marketCategory || $[4] !== props.marketCoverImageNsfw || $[5] !== props.marketCoverImageUrl || $[6] !== props.marketPubkey || $[7] !== props.marketTicker || $[8] !== props.marketTitle) {
    t1 = isWinnerPage ? <WinnerPageHeader marketPubkey={props.marketPubkey} marketTitle={props.marketTitle ?? ""} marketTicker={props.marketTicker ?? null} marketCategory={props.marketCategory ?? ""} coverImageUrl={props.marketCoverImageUrl ?? null} coverImageNsfw={props.marketCoverImageNsfw === true} /> : null;
    $[2] = isWinnerPage;
    $[3] = props.marketCategory;
    $[4] = props.marketCoverImageNsfw;
    $[5] = props.marketCoverImageUrl;
    $[6] = props.marketPubkey;
    $[7] = props.marketTicker;
    $[8] = props.marketTitle;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== isWinnerPage || $[11] !== props.marketTitle) {
    t2 = !isWinnerPage && props.marketTitle ? <h2 className="break-words text-heading font-semibold text-foreground-primary">{props.marketTitle}</h2> : null;
    $[10] = isWinnerPage;
    $[11] = props.marketTitle;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== t) {
    t3 = t("historyHelp");
    $[13] = t;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== t3) {
    t4 = <p className="text-body text-foreground-secondary">{t3}</p>;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== t2 || $[18] !== t4) {
    t5 = <div className="flex min-w-0 flex-col gap-2">{t2}{t4}</div>;
    $[17] = t2;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== isWinnerPage || $[21] !== props.marketPubkey || $[22] !== vm.winner) {
    t6 = !isWinnerPage ? <WinnerHeader winner={vm.winner} marketPubkey={props.marketPubkey} /> : null;
    $[20] = isWinnerPage;
    $[21] = props.marketPubkey;
    $[22] = vm.winner;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== isWinnerPage || $[25] !== props.eventAssociation) {
    t7 = isWinnerPage && props.eventAssociation !== undefined ? <ResolvedEventAssociation {...props.eventAssociation} /> : null;
    $[24] = isWinnerPage;
    $[25] = props.eventAssociation;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== vm.stats) {
    t8 = <StatStrip stats={vm.stats} />;
    $[27] = vm.stats;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== props.marketPubkey || $[30] !== vm.outcomeRows) {
    t9 = <OtherOutcomesTable rows={vm.outcomeRows} marketPubkey={props.marketPubkey} />;
    $[29] = props.marketPubkey;
    $[30] = vm.outcomeRows;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== t1 || $[33] !== t5 || $[34] !== t6 || $[35] !== t7 || $[36] !== t8 || $[37] !== t9) {
    t10 = <>{t1}{t5}{t6}{t7}{t8}{t9}</>;
    $[32] = t1;
    $[33] = t5;
    $[34] = t6;
    $[35] = t7;
    $[36] = t8;
    $[37] = t9;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  const content = t10;
  if (isBare) {
    let t11;
    if ($[39] !== t) {
      t11 = t("regionLabel");
      $[39] = t;
      $[40] = t11;
    } else {
      t11 = $[40];
    }
    let t12;
    if ($[41] !== isWinnerPage || $[42] !== props.marketPubkey || $[43] !== t) {
      t12 = isWinnerPage ? <AnimatedButton asChild={true} variant="secondary" size="lg" className="w-full"><Link href={`/markets/${props.marketPubkey}`}>{t("viewMarket")}</Link></AnimatedButton> : null;
      $[41] = isWinnerPage;
      $[42] = props.marketPubkey;
      $[43] = t;
      $[44] = t12;
    } else {
      t12 = $[44];
    }
    let t13;
    if ($[45] !== content || $[46] !== t11 || $[47] !== t12) {
      t13 = <section aria-label={t11} data-testid="resolved-panel" className="flex flex-col gap-4">{content}{t12}</section>;
      $[45] = content;
      $[46] = t11;
      $[47] = t12;
      $[48] = t13;
    } else {
      t13 = $[48];
    }
    return t13;
  }
  let t11;
  if ($[49] !== t) {
    t11 = t("regionLabel");
    $[49] = t;
    $[50] = t11;
  } else {
    t11 = $[50];
  }
  let t12;
  if ($[51] !== content) {
    t12 = <div className="flex flex-col gap-4 px-4">{content}</div>;
    $[51] = content;
    $[52] = t12;
  } else {
    t12 = $[52];
  }
  let t13;
  if ($[53] !== t11 || $[54] !== t12) {
    t13 = <Panel role="region" aria-label={t11} data-testid="resolved-panel">{t12}</Panel>;
    $[53] = t11;
    $[54] = t12;
    $[55] = t13;
  } else {
    t13 = $[55];
  }
  return t13;
}